import type { GridCustomCellProps } from "@tql/ui-components-react/kendo/grid";
import { Button } from "@tql/ui-components-react/tql/buttons";
import { shieldTick } from "@tql/ui-components-react/untitled/icons";

import { GridTd } from "~/shared/components/trax-grid/grid-td/grid-td.tsx";
import type { QuoteBoardItem } from "~/shared/schemas/quote-board-schema.ts";
import { formatCurrency } from "~/shared/utils/string-fns.ts";

import {
	canAddInsuranceQuoteBoardRow,
	resolveQuoteBoardStatus,
	shouldHideQuoteBoardInsuranceCell,
} from "../../../utils/quote-board-status.ts";

type BulkTenderInsuranceCellProps = Pick<GridCustomCellProps, "tdProps"> & {
	dataItem: QuoteBoardItem;
	hasInsurance?: boolean;
	onOpenInsurance?: (quoteId: number) => void;
};

export const BulkTenderInsuranceCell = ({
	dataItem,
	hasInsurance = false,
	onOpenInsurance,
	...cellProps
}: BulkTenderInsuranceCellProps) => {
	const { rowKind, status } = resolveQuoteBoardStatus(dataItem);
	if (shouldHideQuoteBoardInsuranceCell(dataItem)) {
		return <GridTd tdProps={cellProps.tdProps} />;
	}

	const canChangeInsurance =
		rowKind === "bulk-tender" &&
		status !== "tendered" &&
		Boolean(onOpenInsurance);
	const canAddInsurance =
		canAddInsuranceQuoteBoardRow(dataItem) && Boolean(onOpenInsurance);
	const actionLabel = hasInsurance
		? `Edit insurance for quote ${dataItem.quoteId}`
		: `Add insurance for quote ${dataItem.quoteId}`;
	const renderInsuranceAction = () => {
		if (hasInsurance) {
			if (!canChangeInsurance) return null;
			return (
				<Button
					aria-label={actionLabel}
					onClick={() => onOpenInsurance?.(dataItem.quoteId)}
					svgIcon={shieldTick}
					title={actionLabel}
					variant={"primary"}
				/>
			);
		}
		if (!canAddInsurance) return null;
		return (
			<Button
				aria-label={actionLabel}
				onClick={() => onOpenInsurance?.(dataItem.quoteId)}
				svgIcon={shieldTick}
				title={actionLabel}
				variant={"secondary"}
			>
				Add
			</Button>
		);
	};

	let liability = "\u2014";
	if (
		hasInsurance &&
		dataItem.insuranceValue != null &&
		dataItem.insuranceValue > 0
	) {
		liability = formatCurrency(dataItem.insuranceValue);
	} else if (dataItem.selectedCarrierPriceId != null) {
		liability = `${formatCurrency(dataItem.maxLiabilityNew ?? 0)} / ${formatCurrency(dataItem.maxLiabilityUsed ?? 0)}`;
	}

	return (
		<GridTd tdProps={cellProps.tdProps}>
			<div className={"quote-board-insurance-cell"}>
				<span>{liability}</span>
				{renderInsuranceAction()}
			</div>
		</GridTd>
	);
};
